import store from '@/store';
import user from '@/components/user';
import api, { toGraphQLQuery, getGraphQLHeaders } from '@/api';
import log from '@/configured/logger';
import constants from '@/constants';
import { localeToCraftSiteHandle } from '@/configured/languages/config';
import {
  isBootstrapRequest,
  normalizeBootstrapOptions,
  runSharedLoad,
} from '@/base-data/shared-load';

/**
 * Helper to execute a GraphQL query with automatic fallback to English
 * when the current language returns no data.
 */
async function fetchWithFallback(queryBuilder, extractData, queryName) {
  const site = localeToCraftSiteHandle(store.state.i18n.language);

  const executeQuery = (siteCode) => {
    const query = queryBuilder(siteCode);
    return api.get(constants.GRAPH_QL_ENDPOINT, {
      params: { query: toGraphQLQuery(query) },
      headers: getGraphQLHeaders(),
    });
  };

  try {
    const response = await executeQuery(site);
    const data = extractData(response);
    
    // If no data and not already English, fall back to English
    if (!data && site !== 'en') {
      log.info(`No data for ${queryName} in ${site}, falling back to English`);
      const fallbackResponse = await executeQuery('en');
      return extractData(fallbackResponse);
    }
    
    return data;
  } catch (error) {
    if (api.isCancel(error)) {
      log.info(`Caught cancellation of ${queryName}`);
      return null;
    }
    
    // On error for non-English, try English as fallback
    if (site !== 'en') {
      log.info(`Error loading ${queryName} for ${site}, trying English fallback`);
      try {
        const fallbackResponse = await executeQuery('en');
        return extractData(fallbackResponse);
      } catch (fallbackError) {
        log.error(`Error loading ${queryName} fallback`, fallbackError);
        return null;
      }
    }
    
    log.error(`Error loading ${queryName}`, error);
    return null;
  }
}

const exports = {
  namespace: 'home',
  module: {
    namespaced: true,
    state: {
      homeMarquee: {},
      homeDefaultRegionalHighlights: {},
      homeRegionalHighlights: {},
      homeFavorites: {},
      homeSalesTraining: {},
      homeProductInfo: {},
      loaded: false,
    },
    mutations: {
      SET_HOME_MARQUEE: (state, homeMarquee) => {
        state.homeMarquee = homeMarquee;
      },
      SET_HOME_DEFAULT_REGIONAL_HIGHLIGHTS: (state, regionalHighlights) => {
        state.homeDefaultRegionalHighlights = regionalHighlights;
      },
      SET_HOME_REGIONAL_HIGHLIGHTS: (state, regionalHighlights) => {
        state.homeRegionalHighlights = regionalHighlights;
      },
      SET_HOME_FAVORITES: (state, favorites) => {
        state.homeFavorites = favorites;
      },
      SET_HOME_SALES_TRAINING: (state, salesTraining) => {
        state.homeSalesTraining = salesTraining;
      },
      SET_HOME_PRODUCT_INFO: (state, productInfo) => {
        state.homeProductInfo = productInfo;
      },
      SET_LOADED: (state, loaded) => {
        state.loaded = loaded;
      },
    },
    actions: {
      loadHomeDataIfNeeded,
      loadHomeMarquee,
      loadHomeRegionalHighlights,
      loadDefaultHighlights,
      loadHomeFavorites,
      loadHomeSalesTraining,
      loadHomeProductInfo,
    },
    getters: {
      homeMarquee: (state) => state.homeMarquee,
      homeRegionalHighlights: (state) => {
        const highlights = [...state.homeDefaultRegionalHighlights];
        for (let i = 0; i < highlights.length; i++) {
          if (state.homeRegionalHighlights[i]) {
            highlights[i] = state.homeRegionalHighlights[i];
          }
        }
        return highlights;
      },
      homeFavorites: (state) => state.homeFavorites,
      homeProductInfo: (state) => state.homeProductInfo,
    },
  },
};

store.registerModule(exports.namespace.split('/'), exports.module);

export default exports;

async function loadHomeDataIfNeeded({ state, commit }, payload = false) {
  const { forceInline, mode, bootstrapContext } =
    normalizeBootstrapOptions(payload);

  if (state.loaded && !forceInline) {
    if (isBootstrapRequest(bootstrapContext)) {
      return Promise.resolve();
    }

    log.info('loadHomeDataIfNeeded: optimistic reload');
    // Always request these to ensure fresh data, but let the page come up
    // to manage performance.
    store.dispatch(`${exports.namespace}/loadHomeMarquee`);
    store.dispatch(`${exports.namespace}/loadHomeRegionalHighlights`);
    store.dispatch(`${exports.namespace}/loadDefaultHighlights`);
    store.dispatch(`${exports.namespace}/loadHomeFavorites`);
    store.dispatch(`${exports.namespace}/loadHomeProductInfo`);
    store.dispatch(`${exports.namespace}/loadHomeSalesTraining`);
    return Promise.resolve();
  }

  log.info('loadHomeDataIfNeeded: needed data');
  const language = store.state.i18n.language || 'en';

  return runSharedLoad(
    `${exports.namespace}:${language}`,
    () =>
      Promise.all([
        store.dispatch(`${exports.namespace}/loadHomeMarquee`),
        store.dispatch(`${exports.namespace}/loadHomeRegionalHighlights`),
        store.dispatch(`${exports.namespace}/loadDefaultHighlights`),
        store.dispatch(`${exports.namespace}/loadHomeFavorites`),
        store.dispatch(`${exports.namespace}/loadHomeProductInfo`),
        store.dispatch(`${exports.namespace}/loadHomeSalesTraining`),
      ]).then(() => {
        commit('SET_LOADED', true);
      }),
    {
      bootstrapContext,
      metricKey: exports.namespace,
    }
  );
}

async function loadHomeProductInfo({ state, commit }) {
  const queryBuilder = (site) => `{
    entry(section: "home", site: "${site}") {
      ... on home_home_Entry {
        productInformationHeadline
        productInformationSubhead
        productInformationDescription
        productInformationCtaText
        productInformationCtaLink
        productInformationMainVideoHeadline
        productInformationMainVideoSubhead
        productInformationMainVideoImage {
          url @transform (width: 1400)
        }
        productInformationMainVideoLink
        productInformationSecondaryVideoHeadline
        productInformationSecondaryVideoImage {
          url @transform (width: 1400)
        }
        productInformationSecondaryVideoLink
        productInformationSecondaryVideoCategory {
          title
        }
      }
    }
  }`;

  const data = await fetchWithFallback(
    queryBuilder,
    (response) => response.data.entry,
    'home product info'
  );
  
  if (data) {
    log.info('Loaded home product info', { data });
    commit('SET_HOME_PRODUCT_INFO', data);
  }
}

async function loadHomeMarquee({ state, commit }) {
  const queryBuilder = (site) => `{
    entry(section: "home", site: "${site}") {
      ... on home_home_Entry {
        marqueeHeadline
        marqueeSubhead
        mainImage {
          url @transform (width: 1400)
        }
        marqueeProductsHeadline
        marqueeProductsText
        marqueeProductsLinkText
        marqueeTrainingHeadline
        marqueeTrainingText
        marqueeTrainingLinkText
      }
    }
  }`;

  const data = await fetchWithFallback(
    queryBuilder,
    (response) => response.data.entry,
    'home marquee'
  );
  
  if (data) {
    log.info('Loaded home marquee data', { data });
    commit('SET_HOME_MARQUEE', data);
  }
}

// GraphQL returns a highly nested array that is difficult to use, lets flatten
// it for easy of use.
function flattenRegionalHighlightsArray(highlights) {
  return highlights && highlights.regionalHighlights
    ? highlights.regionalHighlights.map((value) => {
        if (
          value &&
          'highlightedContent' in value &&
          value.highlightedContent.length === 1
        ) {
          return value.highlightedContent[0];
        }
        return null;
      })
    : [];
}

async function loadHomeRegionalHighlights({ state, commit }) {
  const regionId = store.getters[`${user.store.namespace}/regionId`];
  
  const queryBuilder = (site) => `{
    entry(section: "locations", id: ${regionId}, site: "${site}") {
      ... on locations_regions_Entry {
        regionalHighlights {
          ... on regionalHighlights_BlockType {
            highlightedContent {
              ... on regionalHighlights_regionalHighlights_Entry {
                regionalHighlightHeadline
                regionalHighlightSubhead
                regionalHighlightCopy
                regionalHighlightBurst
                regionalHighlightImage {
                  url @transform (width: 1400)
                }
                regionalHighlightLink {
                  ... on regionalHighlightLink_highlightLink_BlockType {
                    linkText
                    linkUrl
                  }                  
                }
              }
            }
          }
        }
      }
    }
  }`;

  const data = await fetchWithFallback(
    queryBuilder,
    (response) => response.data.entry,
    'home regional highlights'
  );
  
  log.info('Loaded home regional highlights data', { data });
  commit(
    'SET_HOME_REGIONAL_HIGHLIGHTS',
    flattenRegionalHighlightsArray(data)
  );
}

async function loadDefaultHighlights({ state, commit }) {
  const queryBuilder = (site) => `{
    entry(section: "home", site: "${site}") {
      ... on home_home_Entry {
        regionalHighlights {
          ... on regionalHighlights_BlockType {
            highlightedContent {
              ... on regionalHighlights_regionalHighlights_Entry {
                regionalHighlightHeadline
                regionalHighlightSubhead
                regionalHighlightCopy
                regionalHighlightBurst
                regionalHighlightImage {
                  url @transform (width: 1400)
                }
                regionalHighlightLink {
                  ... on regionalHighlightLink_highlightLink_BlockType {
                    linkText
                    linkUrl
                  }                  
                }
              }
            }
          }
        }
      }
    }
  }`;

  const data = await fetchWithFallback(
    queryBuilder,
    (response) => response.data.entry,
    'home default highlights'
  );
  
  log.info('Loaded home default highlights data', { data });
  commit(
    'SET_HOME_DEFAULT_REGIONAL_HIGHLIGHTS',
    flattenRegionalHighlightsArray(data)
  );
}

async function loadHomeSalesTraining({ state, commit }) {
  const queryBuilder = (site) => `{
      entry(section: "home", site: "${site}") {
        ... on home_home_Entry {
					salesTrainingHeadline
          salesTrainingSubhead
          salesTrainingDescription
          salesTrainingCtaText
          salesTrainingInProgressHeadline
          salesTrainingBenefitsHeadline
          salesTrainingBenefitsVideoLink
          salesTrainingBenefitsImage {
            url @transform (width: 1400)
          }
          trainingFeaturesEmptyStateHeadline
          trainingFeaturesEmptyStateSubhead
          trainingFeaturesEmptyStateCopy
          trainingFeaturesEmptyStateVideoLink
          trainingFeaturesEmptyStateVideoImage {
            url @transform (width: 1400)
          }
          trainingFeaturesEmptyStateVideoHeadline
          trainingFeaturesEmptyStateVideoSubhead
        }
      }
    }`;

  const data = await fetchWithFallback(
    queryBuilder,
    (response) => response.data.entry,
    'home sales training'
  );
  
  if (data) {
    log.info('Loaded home sales training data', { data });
    commit('SET_HOME_SALES_TRAINING', data);
  }
}

async function loadHomeFavorites({ state, commit }) {
  const queryBuilder = (site) => `{
    entry(section: "home", site: "${site}") {
      ... on home_home_Entry {
        favoritesHeadline
        favoritesEmptyStateHeadline
        favoritesEmptyStateCopy
        favoritesEmptyStateLinkText
        favoritesEmptyStateBackgroundImage {
          url @transform (width: 1400)
        }
        favoritesEmptyStateFeaturedProduct {
          ... on products_products_Entry {
            id
            slug
            title
            mainImage {
              url @transform (width:1400)
            }
            newProductCategory {
              title
            }
          }
        }
      }
    }
  }`;

  const data = await fetchWithFallback(
    queryBuilder,
    (response) => response.data.entry,
    'home favorites'
  );
  
  if (data) {
    log.info('Loaded home favorites data', { data });
    commit('SET_HOME_FAVORITES', data);
  }
}
